import { PencilIcon, TrashIcon } from '@heroicons/react/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { States } from '../../../../../constants';
import { developerApplicationSelector } from '../../../../../store';
import {
  CompanyCompletedProjectInterface,
  setExperience,
} from '../../../../../store/DeveloperApplication/Experience';
import Button from '../../../../UI/Button';
import { v4 as uuidv4 } from 'uuid';
import {
  companyRoles,
  constructionTypes,
  projectTypes,
  validationSchema,
} from './constant';
import { useEffect, useState } from 'react';
import Confirm from '../../../../UI/Modal/Confirm';
import { HookFormProvider } from '../../../../UI/HookForm/Provider';
import { HookForm } from '../../../../UI/HookForm/Form';
import { HookInput } from '../../../../UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '../../../../UI/HookForm/Form/ReactSelect';
import HookMaskedInput from '../../../../UI/HookForm/Form/MaskedInput';
import { HookCheckboxGroup } from '../../../../UI/HookForm/Form/CheckBoxGroup';
import { emptyCompanyCompletedProject } from '../PrincipalCompletedProject/constant';
import { formatDate3 } from '../../../../../utils';

//Dummy Comment

const CompanyCompletedProjectBase = () => {
  const dispatch = useDispatch();
  const { experience } = useSelector(developerApplicationSelector);
  const companyCompletedProjects = experience.companyCompleted;
  const [formErrors, setFormErrors] = useState('');
  const [companyRoleError, setCompanyRoleError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [selectedProject, setSelectedProject] =
    useState<CompanyCompletedProjectInterface>();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const {
    formState: { isSubmitSuccessful, errors },
    reset,
    register,
    setValue,
    getValues,
    trigger,
  } = useFormContext();

  const editProjectHandler = (project: any) => {
    setIsEditing(true);
    Object.keys(project).forEach((key) => {
      if (key === 'state') {
        setValue('state', { label: project[key], value: project[key] });
      } else if (key === 'dateCompleted') {
        setValue(
          'dateCompleted',
          formatDate3(project[key].toString(), 'yyyy-mm-dd')
        );
      } else {
        setValue(key, project[key]);
      }
    });
  };

  const updateProjectHandler = () => {
    if (!companyRoleValidation()) {
      setCompanyRoleError("Company's Role is required");
      return;
    } else {
      setCompanyRoleError('');
    }
    trigger().then((isValid) => {
      if (isValid) {
        let updatedProjects: any[] = [...companyCompletedProjects];
        const currentValue = getValues();
        const existingProjectIndex = updatedProjects.findIndex(
          (p) => p.id === currentValue.id
        );

        if (existingProjectIndex > -1) {
          updatedProjects[existingProjectIndex] = {
            ...currentValue,
            state: currentValue.state.value,
          };
        }

        dispatch(setExperience({ companyCompleted: updatedProjects }));
        reset();
        setIsEditing(false);
        setFormErrors('');
      } else {
        onError();
      }
    });
  };

  const removeProjectHandler = (project: CompanyCompletedProjectInterface) => {
    setShowRemoveConfirm(true);
    setSelectedProject(project);
  };
  const confirmRemoveProject = async () => {
    setShowRemoveConfirm(false);
    const filteredProjects = companyCompletedProjects.filter(
      (project) => project.id !== selectedProject?.id
    );
    dispatch(setExperience({ companyCompleted: filteredProjects }));
    if (selectedProject?.id === getValues().id) {
      reset();
      setIsEditing(false);
    }
  };

  const cancelRemoveProject = () => {
    setShowRemoveConfirm(false);
  };

  const companyRoleValidation = () => {
    //custom input validation due to seperate input type
    //for Company role Partial owner field
    const companyRoleisValid =
      getValues('companyRole')?.length > 0 ||
      parseInt(getValues('partialOwner')) > 0;
    return companyRoleisValid;
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(emptyCompanyCompletedProject, {
        keepTouched: false,
        keepErrors: false,
      });
    }
  }, [isSubmitSuccessful]);

  const onSubmit = (data: any) => {
    if (!companyRoleValidation()) {
      onError();
      return;
    } else {
      setCompanyRoleError('');
    }
    const projects = [...companyCompletedProjects];
    projects.push({
      ...data,
      id: uuidv4(),
    });

    dispatch(setExperience({ companyCompleted: projects }));
    setFormErrors('');
  };

  const onError = () => {
    if (!companyRoleValidation()) {
      setCompanyRoleError("Company's Role is required");
    } else {
      setCompanyRoleError('');
    }
    setFormErrors('Form is invalid. Please fix the highlighted form fields.');
  };

  return (
    <>
      {showRemoveConfirm && (
        <Confirm
          title='Remove Project'
          message={`You are about to remove ${selectedProject?.projectName} project. Are you sure you want to remove ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={confirmRemoveProject}
          decline={cancelRemoveProject}
        />
      )}
      <p className='block text-sm text-neutralColor-dark'>
        Please provide the following information for the last 3 projects
        completed by the company. If the project is still underway, please
        specify the status.
      </p>
      <div className='space-y-2'>
        {companyCompletedProjects.length > 0 &&
          companyCompletedProjects.map(
            (project: CompanyCompletedProjectInterface) => (
              <div
                key={project.id}
                className='flex justify-between items-center bg-white border px-4 py-2 rounded-md'
              >
                <div>
                  <p className='font-bold'>{project.projectName}</p>
                  <span className='text-neutralColor-normal'>
                    {project.streetAddress}, {project.city},
                    {project.state.value},{project.zipCode}
                  </span>
                </div>
                <div className='flex gap-4'>
                  <div
                    className='bg-gray-100 p-2 rounded-full hover:bg-blue-200 cursor-pointer'
                    onClick={() => editProjectHandler(project)}
                  >
                    <PencilIcon className='h-5 w-5 text-blue-500' />
                  </div>
                  <div
                    className='bg-gray-100 p-2 rounded-full hover:bg-red-200 cursor-pointer'
                    onClick={() => removeProjectHandler(project)}
                  >
                    <TrashIcon className='h-5 w-5 text-tertiaryColor-normal' />
                  </div>
                </div>
              </div>
            )
          )}
      </div>
      <HookForm noDefaultClass onSubmit={onSubmit}>
        <div className='w-full  grid grid-cols-6 gap-6'>
          <HookInput
            customClassName='col-span-3'
            label='Project Name'
            id='companyCompletedProjectName'
            name='projectName'
            type='text'
          />
          <HookInput
            customClassName='col-span-6 sm:col-span-3'
            label='Date Completed (U&O)'
            id='companyCompletedDateCompleted'
            name='dateCompleted'
            type='date'
          />
          <HookInput
            customClassName='col-span-6'
            label='Street Address'
            id='companyCompletedStreetAddress'
            name='streetAddress'
            type='text'
          />
          <HookInput
            customClassName='col-span-6 sm:col-span-2'
            label='City'
            id='companyCompletedCity'
            name='city'
            type='text'
          />
          <HookReactSingleSelect
            componentClassName='col-span-6 sm:col-span-3 lg:col-span-2'
            label='State / Province'
            id='companyCompletedState'
            name='state'
            options={States.map((s) => ({ label: s.name, value: s.name }))}
          />

          <HookInput
            customClassName='col-span-6 sm:col-span-2'
            label='ZIP / Postal code'
            id='companyCompletedZipCode'
            name='zipCode'
            type='number'
          />
          <HookMaskedInput
            customClassName='col-span-6 sm:col-span-2'
            label='Gross Building Area'
            name='grossBuildingArea'
            id='companyCompletedGrossBuildingArea'
            maskedComponent='numeric'
          />
          <HookMaskedInput
            customClassName='col-span-6 sm:col-span-2'
            label='Number of Housing units (if applicable)'
            name='housingUnits'
            id='companyCompletedHousingUnits'
            maskedComponent='numeric'
          />
          <HookMaskedInput
            customClassName='col-span-6 sm:col-span-2'
            label='Total Development Cost'
            name='developmentCost'
            id='companyCompletedTotalDevelopmentCost'
            maskedComponent='currency'
          />
          <div className='col-span-6 md:col-span-3'>
            <HookCheckboxGroup
              label='Project Type (check all that apply):'
              id='companyCompletedProjectTypes'
              name='projectTypes'
              options={projectTypes.map((p) => ({
                label: p.label,
                value: p.name,
              }))}
            />
          </div>
          <div className='col-span-6 md:col-span-3'>
            <HookCheckboxGroup
              label='Construction Type (check all that apply):'
              id='companyCompletedConstructionTypes'
              name='constructionTypes'
              options={constructionTypes.map((c) => ({
                label: c.label,
                value: c.label,
              }))}
            />
          </div>
          <div className='col-span-6'>
            <div onClick={() => setCompanyRoleError('')}>
              <HookCheckboxGroup
                label="Please select the company's role (Check all that apply):"
                id='companyCompletedCompanyRoles'
                name='companyRole'
                options={companyRoles.map((c) => ({
                  label: c.label,
                  value: c.name,
                }))}
              />
            </div>
            <fieldset className='space-y-2 mt-2'>
              <div className='relative flex items-start'>
                {/* <div className="flex items-center h-5">
                    <input
                      id="companyCompletedPartialOwner"
                      aria-describedby="companyCompletedPartialOwner"
                      type="checkbox"
                      className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 rounded"
                      {...register("companyCompletedPartialOwner")}
                    />
                  </div> */}
                <div className='text-sm flex w-full gap-2'>
                  <label
                    htmlFor='companyCompletedPartialOwner'
                    className='font-medium text-gray-700'
                  >
                    Partial Owner (Please note percentage):
                  </label>
                  <input
                    type='number'
                    id='companyCompletedPartialOwner'
                    autoComplete='companyCompletedPartialOwner'
                    className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm'
                    {...register('partialOwner')}
                  />
                  <span>%</span>
                </div>
              </div>
              {errors.partialOwner && (
                <p className='text-tertiaryColor-normal'>
                  {errors.partialOwner?.message}
                </p>
              )}
              {companyRoleError && (
                <p className='text-tertiaryColor-normal'>{companyRoleError}</p>
              )}
            </fieldset>
          </div>
          {formErrors.length > 0 && (
            <p className='col-span-6 text-tertiaryColor-normal text-md mb-5'>
              {formErrors}
            </p>
          )}
          {!isEditing && (
            <Button label='Add Project' type='submit' btnStyle='btn-primary' />
          )}
          {isEditing && (
            <Button
              label='Save'
              type='button'
              btnStyle='btn-primary'
              onClick={updateProjectHandler}
            />
          )}
        </div>
      </HookForm>
    </>
  );
};

const CompanyCompletedProjectForm = () => {
  const { experience } = useSelector(developerApplicationSelector);

  return (
    <HookFormProvider resolver={yupResolver(validationSchema)} mode='all'>
      <CompanyCompletedProjectBase />
    </HookFormProvider>
  );
};

export default CompanyCompletedProjectForm;
